import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { ButtonGroup } from "~/components/ui/button-group";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";

import { FormattedChartData, SensorData } from "./types";
import { SingleSensorDataCharts } from "./SingleSensorDataCharts";
import { processAccelerometerData } from "./accelerometerData.utils";
import {
  ResistanceViewMode,
  transformLinesForCapacitanceView,
  transformLinesForResistanceView,
} from "./derivative.utils";
import { LazyChartSection } from "./LazyChartSection";
import { LineChartSection } from "./LineChartSection";

interface SensorDataChartsProps {
  tests: {
    sampleFileUrl: string;
    name?: string;
  }[];
}

export const SensorDataCharts: React.FC<SensorDataChartsProps> = ({
  tests,
}) => {
  const [testsData, setTestsData] = useState<(SensorData | null)[]>([]);
  const [hasE12Error, setHasE12Error] = useState<boolean[]>([]);
  const [hasHighRH, setHasHighRH] = useState<boolean[]>([]);
  const isSingleTest = tests.length === 1;
  const [testNameMap, setTestNameMap] = useState<Map<string, number>>(
    new Map()
  );
  const [resistanceChannelView, setResistanceChannelView] = useState<
    Record<number, ResistanceViewMode>
  >({});
  const [resistanceChannelSmoothed, setResistanceChannelSmoothed] = useState<
    Record<number, boolean>
  >({});
  const [capacitanceChannelView, setCapacitanceChannelView] = useState<
    Record<number, ResistanceViewMode>
  >({});
  const [capacitanceChannelSmoothed, setCapacitanceChannelSmoothed] = useState<
    Record<number, boolean>
  >({});

  const getUniqueTestName = (testName: string, testIndex: number) => {
    const baseName = testName || `Test ${testIndex + 1}`;
    const count = testNameMap.get(baseName) || 0;

    if (count > 1) {
      const previousOccurrences = tests
        .slice(0, testIndex)
        .filter((t) => (t.name || "Test") === baseName).length;
      return `${baseName} (${previousOccurrences + 1})`;
    }

    return baseName;
  };

  useEffect(() => {
    const nameMap = new Map<string, number>();
    tests.forEach((test) => {
      const name = test.name || "Test";
      nameMap.set(name, (nameMap.get(name) || 0) + 1);
    });
    setTestNameMap(nameMap);
  }, [tests]);

  useEffect(() => {
    const processAllTests = async () => {
      const processedTests = await Promise.all(
        tests.map(async (test, index) => {
          try {
            const csvUrl = test.sampleFileUrl.replace(".json", ".csv");
            const response = await fetch(csvUrl);
            const csvText = await response.text();

            const parsed = Papa.parse(csvText, { header: true });
            const rows = parsed.data as Record<string, string>[];

            const processedData: SensorData = {
              resistance: [],
              capacitance: [],
              trh: [],
              accelerometer: [],
            };

            rows.forEach((row) => {
              const timestamp = row["timestamp"] || "";
              const resistanceValues = Array.from({ length: 6 }, (_, i) =>
                Number(row[`rSensor.Sensor${i + 1}`])
              );
              processedData.resistance.push({
                timestamp,
                values: resistanceValues,
              });

              const capacitanceValues = Array.from({ length: 6 }, (_, i) =>
                Number(row[`cSensor.Sensor${i + 1}`])
              );
              processedData.capacitance.push({
                timestamp,
                values: capacitanceValues,
              });

              processedData.trh.push({
                timestamp,
                cassetteTemp: Number(row["cassette.t"]),
                cassetteRH: Number(row["cassette.rh"]),
                internalTemp: Number(row["internal.t"]),
                internalRH: Number(row["internal.rh"]),
                externalTemp: Number(row["external.t"]),
                externalRH: Number(row["external.rh"]),
              });

              const x = Number(row["accelerometer.x"]);
              const y = Number(row["accelerometer.y"]);
              const z = Number(row["accelerometer.z"]);

              if (!Number.isNaN(x) || !Number.isNaN(y) || !Number.isNaN(z)) {
                processedData.accelerometer.push({
                  timestamp,
                  x: Number.isNaN(x) ? 0 : x,
                  y: Number.isNaN(y) ? 0 : y,
                  z: Number.isNaN(z) ? 0 : z,
                });
              }
            });

            const hasHighResistance = processedData.resistance.some((reading) =>
              reading.values.some(
                (value) => value > 2e12 && Number.isFinite(value)
              )
            );

            const first10RH = processedData.trh.slice(0, 10);
            const hasHighHumidity = first10RH.some(
              (reading) => reading.cassetteRH > 93
            );

            return {
              data: processedData,
              hasE12Error: hasHighResistance,
              hasHighRH: hasHighHumidity,
            };
          } catch (error) {
            console.error(`Error processing test ${index + 1}:`, error);
            toast.error(`Failed to process test ${index + 1}`);
            return null;
          }
        })
      );

      setTestsData(processedTests.map((t) => t?.data ?? null));
      setHasE12Error(processedTests.map((t) => t?.hasE12Error ?? false));
      setHasHighRH(processedTests.map((t) => t?.hasHighRH ?? false));
    };

    processAllTests();
  }, [tests]);

  const accelerometerData = useMemo(() => {
    const testNames = tests.map((test, index) =>
      getUniqueTestName(test.name ?? "Test", index)
    );
    return processAccelerometerData(testsData, testNames);
  }, [testsData, tests, testNameMap]);

  if (isSingleTest) {
    return <SingleSensorDataCharts tests={tests} />;
  }

  if (testsData.length === 0) {
    return <p className="text-sm text-muted-foreground">Processing data...</p>;
  }

  const viewOptions: Array<{
    value: ResistanceViewMode;
    label: string;
  }> = [
    { value: "raw", label: "Raw" },
    { value: "firstDerivative", label: "1st Derivative" },
    { value: "secondDerivative", label: "2nd Derivative" },
  ];

  const formatResistanceTick = (
    value: number,
    view: ResistanceViewMode,
    isSmoothed: boolean
  ) => {
    if (!Number.isFinite(value)) {
      return "";
    }
    if (view === "raw" && !isSmoothed) {
      return value.toExponential(0);
    }
    if (view === "raw" && isSmoothed) {
      return value.toFixed(2);
    }
    return value.toExponential(2);
  };

  const formatResistanceTooltip = (
    value: number,
    view: ResistanceViewMode,
    isSmoothed: boolean
  ) => {
    if (!Number.isFinite(value)) {
      return "NaN";
    }
    if (view === "raw" && !isSmoothed) {
      return value.toExponential(4);
    }
    if (view === "raw" && isSmoothed) {
      return value.toFixed(4);
    }
    return value.toExponential(3);
  };

  const formatSensorChannelData = (
    metric: "resistance" | "capacitance",
    channel: number
  ): FormattedChartData[] => {
    return testsData
      .map((testData, testIndex) => {
        if (!testData) {
          return null;
        }
        const uniqueName = getUniqueTestName(
          tests[testIndex].name ?? "Test",
          testIndex
        );
        const maxThreshold = metric === "capacitance" ? 1e-9 : Infinity;
        return {
          id: uniqueName,
          data: testData[metric].slice(1).map((reading, i) => ({
            x: i,
            y:
              metric === "capacitance" &&
              Number(reading.values[channel]) > maxThreshold
                ? Number.NaN
                : Number(reading.values[channel]),
          })),
        };
      })
      .filter(Boolean) as FormattedChartData[];
  };

  const formatCassetteTRHData = (): FormattedChartData[] => {
    const lines: FormattedChartData[] = [];

    testsData.forEach((testData, testIndex) => {
      if (!testData) {
        return;
      }

      const uniqueName = getUniqueTestName(
        tests[testIndex].name ?? "Test",
        testIndex
      );

      lines.push({
        id: `${uniqueName} - Cassette Temp`,
        data: testData.trh.slice(1).map((d, i) => ({
          x: i,
          y: Number(d.cassetteTemp),
        })),
      });

      lines.push({
        id: `${uniqueName} - Cassette RH`,
        data: testData.trh.slice(1).map((d, i) => ({
          x: i,
          y: Number(d.cassetteRH),
        })),
      });
    });

    return lines;
  };

  const formatEnvironmentTRHData = (): FormattedChartData[] => {
    const lines: FormattedChartData[] = [];

    testsData.forEach((testData, testIndex) => {
      if (!testData) {
        return;
      }

      const uniqueName = getUniqueTestName(
        tests[testIndex].name ?? "Test",
        testIndex
      );

      lines.push({
        id: `${uniqueName} - Ext Temp`,
        data: testData.trh.slice(1).map((d, i) => ({
          x: i,
          y: Number(d.externalTemp),
        })),
      });

      lines.push({
        id: `${uniqueName} - Ext RH`,
        data: testData.trh.slice(1).map((d, i) => ({
          x: i,
          y: Number(d.externalRH),
        })),
      });

      lines.push({
        id: `${uniqueName} - Int Temp`,
        data: testData.trh.slice(1).map((d, i) => ({
          x: i,
          y: Number(d.internalTemp),
        })),
      });

      lines.push({
        id: `${uniqueName} - Int RH`,
        data: testData.trh.slice(1).map((d, i) => ({
          x: i,
          y: Number(d.internalRH),
        })),
      });
    });

    return lines;
  };

  const renderWarnings = () => (
    <>
      {hasE12Error.map(
        (hasError, index) =>
          hasError && (
            <p
              key={`e12-error-${index}`}
              className="text-sm font-semibold text-destructive"
            >
              Warning: Some resistance values exceed 2e12 in{" "}
              {getUniqueTestName(
                tests[index].name || `Test ${index + 1}`,
                index
              )}
            </p>
          )
      )}
      {hasHighRH.map(
        (hasHigh, index) =>
          hasHigh && (
            <p
              key={`rh-warning-${index}`}
              className="text-sm font-semibold text-destructive"
            >
              Warning:{" "}
              {getUniqueTestName(
                tests[index].name || `Test ${index + 1}`,
                index
              )}{" "}
              begins with high humidity
            </p>
          )
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {renderWarnings()}

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Resistance Channels
          </h2>
          <p className="text-sm text-muted-foreground">
            Log scale view of resistance measurements for each sensor.
          </p>
        </div>
        <div className="grid gap-4">
          {Array.from({ length: 6 }, (_, channel) => {
            const channelView = resistanceChannelView[channel] ?? "raw";
            const isSmoothed = resistanceChannelSmoothed[channel] ?? false;
            const channelLines = formatSensorChannelData("resistance", channel);
            const displayedLines = transformLinesForResistanceView(
              channelLines,
              channelView,
              isSmoothed
            );

            return (
              <LazyChartSection key={`resistance-${channel}`} minHeight={360}>
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <ButtonGroup>
                      {viewOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={
                            channelView === option.value ? "default" : "outline"
                          }
                          onClick={() =>
                            setResistanceChannelView((previous) => {
                              if (previous[channel] === option.value) {
                                return previous;
                              }

                              return {
                                ...previous,
                                [channel]: option.value,
                              };
                            })
                          }
                        >
                          {option.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`resistance-smooth-${channel}`}
                        checked={isSmoothed}
                        onCheckedChange={(checked) =>
                          setResistanceChannelSmoothed((prev) => ({
                            ...prev,
                            [channel]: checked as boolean,
                          }))
                        }
                      />
                      <Label htmlFor={`resistance-smooth-${channel}`}>
                        Smooth
                      </Label>
                    </div>
                  </div>
                  <LineChartSection
                    title={null}
                    lines={displayedLines}
                    yScale={
                      channelView === "raw" && !isSmoothed ? "log" : undefined
                    }
                    skipNonPositive={channelView === "raw" && !isSmoothed}
                    yTickFormatter={(value) =>
                      typeof value === "number"
                        ? formatResistanceTick(value, channelView, isSmoothed)
                        : ""
                    }
                    tooltipFormatter={(value) =>
                      formatResistanceTooltip(value, channelView, isSmoothed)
                    }
                  />
                </div>
              </LazyChartSection>
            );
          })}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Capacitance Channels
          </h2>
          <p className="text-sm text-muted-foreground">
            Linear scale view of capacitance readings per sensor.
          </p>
        </div>
        <div className="grid gap-4">
          {Array.from({ length: 6 }, (_, channel) => {
            const channelView = capacitanceChannelView[channel] ?? "raw";
            const isSmoothed = capacitanceChannelSmoothed[channel] ?? false;
            const channelLines = formatSensorChannelData(
              "capacitance",
              channel
            );
            const displayedLines = transformLinesForCapacitanceView(
              channelLines,
              channelView,
              isSmoothed
            );

            return (
              <LazyChartSection key={`capacitance-${channel}`} minHeight={360}>
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <ButtonGroup>
                      {viewOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={
                            channelView === option.value ? "default" : "outline"
                          }
                          onClick={() =>
                            setCapacitanceChannelView((previous) => {
                              if (previous[channel] === option.value) {
                                return previous;
                              }

                              return {
                                ...previous,
                                [channel]: option.value,
                              };
                            })
                          }
                        >
                          {option.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`capacitance-smooth-${channel}`}
                        checked={isSmoothed}
                        onCheckedChange={(checked) =>
                          setCapacitanceChannelSmoothed((prev) => ({
                            ...prev,
                            [channel]: checked as boolean,
                          }))
                        }
                      />
                      <Label htmlFor={`capacitance-smooth-${channel}`}>
                        Smooth
                      </Label>
                    </div>
                  </div>
                  <LineChartSection
                    title={null}
                    lines={displayedLines}
                    yTickFormatter={(value) =>
                      typeof value === "number" ? value.toExponential(1) : ""
                    }
                    tooltipFormatter={(value) => value.toExponential(4)}
                  />
                </div>
              </LazyChartSection>
            );
          })}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Cassette Temperature &amp; Humidity
          </h2>
          <p className="text-sm text-muted-foreground">
            Cassette temperature (°C) and relative humidity (%).
          </p>
        </div>
        <LazyChartSection minHeight={360}>
          <LineChartSection
            title={null}
            lines={formatCassetteTRHData()}
            yTickFormatter={(value) =>
              typeof value === "number" ? value.toFixed(1) : ""
            }
            tooltipFormatter={(value, name) => {
              const suffix = name.includes("Temp") ? "°C" : "%";
              return `${value.toFixed(1)} ${suffix}`;
            }}
          />
        </LazyChartSection>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Environment Temperature &amp; Humidity
          </h2>
          <p className="text-sm text-muted-foreground">
            External and internal temperature (°C) and humidity (%).
          </p>
        </div>
        <LazyChartSection minHeight={360}>
          <LineChartSection
            title={null}
            lines={formatEnvironmentTRHData()}
            yTickFormatter={(value) =>
              typeof value === "number" ? value.toFixed(1) : ""
            }
            tooltipFormatter={(value, name) => {
              const suffix = name.includes("Temp") ? "°C" : "%";
              return `${value.toFixed(1)} ${suffix}`;
            }}
          />
        </LazyChartSection>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Accelerometer
          </h2>
          <p className="text-sm text-muted-foreground">
            Normalized accelerometer readings across all axes.
          </p>
        </div>
        <div className="grid gap-4">
          <LazyChartSection minHeight={360}>
            <LineChartSection
              title="Normalized X Axis"
              lines={accelerometerData.x}
              yTickFormatter={(value) =>
                typeof value === "number" ? value.toFixed(3) : ""
              }
              tooltipFormatter={(value) => value.toFixed(3)}
              showLegend
            />
          </LazyChartSection>
          <LazyChartSection minHeight={360}>
            <LineChartSection
              title="Normalized Y Axis"
              lines={accelerometerData.y}
              yTickFormatter={(value) =>
                typeof value === "number" ? value.toFixed(3) : ""
              }
              tooltipFormatter={(value) => value.toFixed(3)}
              showLegend
            />
          </LazyChartSection>
          <LazyChartSection minHeight={360}>
            <LineChartSection
              title="Normalized Z Axis"
              lines={accelerometerData.z}
              yTickFormatter={(value) =>
                typeof value === "number" ? value.toFixed(3) : ""
              }
              tooltipFormatter={(value) => value.toFixed(3)}
              showLegend
            />
          </LazyChartSection>
        </div>
      </section>
    </div>
  );
};

export default SensorDataCharts;
